import { useEffect, useState } from 'react';
import {
  WEEKDAYS,
  WEEKDAY_LABELS,
  addWeeks,
  PLAN_FIELDS,
  PLAN_FIELD_LABELS,
  cellIsEmpty,
  standardCodesLabel,
  cellMode,
  stripHtml,
  dateOfWeekday,
  weekKeyOf,
  weekRangeLabel,
  type PlanRow,
  type Weekday,
  type WeekKey,
} from '@td/shared';
import { useLessonPlan, useCopyWeek, useClearWeek, useCopyPlan } from '../hooks/useLessonPlans';
import { PlanCellEditor } from '../components/PlanCellEditor';
import { PlanRowEditor } from '../components/PlanRowEditor';
import { CopyPlanMenu, type CopyScope } from '../components/CopyPlanMenu';
import { todayWeekday } from '../lib/dates';
import { HeaderMenu, MenuSection } from '../components/HeaderMenu';
import { useConfirm } from '../components/ConfirmDialog';
import {
  BoardControls,
  MIN_ZOOM,
  MAX_ZOOM,
  ResizableBoard,
  readBoard,
  readNumber,
  type BoardSize,
} from '../components/ResizableBoard';

const ZOOM_KEY = 'td.plan.zoom';
const BOARD_KEY = 'td.plan.board';

export function LessonPlansPage() {
  const thisWeek = weekKeyOf();
  const [week, setWeek] = useState<WeekKey>(thisWeek);
  const [editingCell, setEditingCell] = useState<{ row: PlanRow; weekday: Weekday } | null>(null);
  const [editingRows, setEditingRows] = useState(false);
  const [copying, setCopying] = useState<CopyScope | null>(null);

  const { data: plan, isLoading, error } = useLessonPlan(week);
  const copyWeek = useCopyWeek();
  const clearWeek = useClearWeek();
  const copyPlan = useCopyPlan();
  const confirm = useConfirm();

  const isCurrentWeek = week === thisWeek;
  const offset = Math.round(
    (new Date(week).getTime() - new Date(thisWeek).getTime()) / (7 * 86_400_000),
  );
  const relative =
    offset === 0
      ? 'This week'
      : offset === 1
        ? 'Next week'
        : offset === -1
          ? 'Last week'
          : offset > 0
            ? `In ${offset} weeks`
            : `${Math.abs(offset)} weeks ago`;

  /*
   * Zoom and the board's size, remembered on this device.
   *
   * A per-page control rather than the app-wide text size in Settings: this
   * one table wants to be legible from across a classroom while the rest of
   * the app stays as it is, and the two are genuinely different wishes.
   */
  const [zoom, setZoom] = useState(readNumber(ZOOM_KEY, 1, MIN_ZOOM, MAX_ZOOM));
  const [board, setBoard] = useState<BoardSize>(() => readBoard(BOARD_KEY));

  useEffect(() => {
    try {
      localStorage.setItem(ZOOM_KEY, String(zoom));
      localStorage.setItem(BOARD_KEY, JSON.stringify(board));
    } catch {
      // Not remembered; it still holds for this session.
    }
  }, [zoom, board]);

  const rows = plan?.rows ?? [];
  const cells = plan?.cells ?? {};

  return (
    <div className="w-full">
      {/*
        The heading and the toolbar keep a readable measure; the plan itself
        gets the whole screen, because a week of five columns is exactly the
        thing that wants the room. It can be dragged smaller from the corner.
      */}
      <div className="mx-auto max-w-[1400px]">
        <header className="page-header flex flex-wrap items-end justify-between gap-3 print:hidden">
          <h1 className="text-2xl">Lesson plans</h1>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setWeek(addWeeks(week, -1))}
                className="btn-secondary px-2 py-1 text-xs"
                aria-label="Previous week"
              >
                ←
              </button>
              <div className="min-w-[140px] px-1 text-center">
                <div className="text-sm text-espresso">{weekRangeLabel(week)}</div>
                <div className="text-[11px] text-stone">{relative}</div>
              </div>
              <button
                onClick={() => setWeek(addWeeks(week, 1))}
                className="btn-secondary px-2 py-1 text-xs"
                aria-label="Next week"
              >
                →
              </button>
              {!isCurrentWeek && (
                <button
                  onClick={() => setWeek(thisWeek)}
                  className="btn-secondary ml-1 px-2.5 py-1 text-xs"
                >
                  This week
                </button>
              )}
            </div>

            <button
              onClick={() => setEditingRows(true)}
              className="btn-secondary px-2.5 py-1 text-xs"
            >
              Schedule
            </button>
            <button onClick={() => window.print()} className="btn-secondary px-2.5 py-1 text-xs">
              Print
            </button>

            {/*
              Copying and clearing a week are things a teacher does on a
              Friday, not forty times a day. They sat across the top as three
              solid buttons and a set of zoom controls, above the plan itself.
            */}
            <HeaderMenu label="Week tools">
              {(close) => (
                <>
                  <MenuSection title="This week">
                    <button
                      className="btn-soft w-full"
                      onClick={() => {
                        copyWeek.mutate({
                          week,
                          fromWeek: addWeeks(week, -1),
                          includeContent: true,
                        });
                        close();
                      }}
                    >
                      Copy last week into this one
                    </button>
                    <button
                      className="btn-soft w-full"
                      onClick={() => {
                        copyPlan.mutate({ from: { week }, to: { week: addWeeks(week, 1) } });
                        close();
                      }}
                    >
                      Copy this week into next week
                    </button>
                    <button
                      className="btn-soft-danger w-full"
                      onClick={async () => {
                        const ok = await confirm({
                          title: 'Clear everything written for this week?',
                          body: 'Your subjects and times stay; only what you typed into the cells goes.',
                          confirmLabel: 'Clear the week',
                          danger: true,
                        });
                        if (ok) clearWeek.mutate({ week });
                        close();
                      }}
                    >
                      Clear this week
                    </button>
                    {copyWeek.isError && (
                      <p className="text-xs text-rust">Nothing saved for last week yet.</p>
                    )}
                  </MenuSection>

                  <MenuSection title="Size">
                    <BoardControls
                      zoom={zoom}
                      onZoom={setZoom}
                      onFit={() => setBoard({ width: null, height: null })}
                    />
                  </MenuSection>
                </>
              )}
            </HeaderMenu>
          </div>
        </header>
      </div>

      <div className="mb-3 hidden print:block">
        <h1 className="text-xl">Lesson plans · {weekRangeLabel(week)}</h1>
      </div>

      {error ? (
        <p className="text-sm text-rust">Could not load that week.</p>
      ) : rows.length === 0 && !isLoading ? (
        <div className="card max-w-lg">
          <p className="mb-3 text-sm text-walnut">
            No subjects in your schedule yet — set one up and it'll carry forward to every new week.
          </p>
          <button onClick={() => setEditingRows(true)} className="btn-primary">
            Set up my schedule
          </button>
        </div>
      ) : (
        <ResizableBoard zoom={zoom} size={board} onResize={setBoard} loading={isLoading}>
          <table className="print-landscape w-full min-w-[900px] table-fixed border-collapse overflow-hidden rounded-card bg-white shadow-soft print:shadow-none">
            <thead>
              <tr className="accent-head">
                <th className="w-[150px] border-b border-r border-sand px-2 py-2 text-left text-xs font-normal text-walnut">
                  Subject
                </th>
                {WEEKDAYS.map((d) => {
                  const isToday = isCurrentWeek && d === todayWeekday();
                  return (
                    <th
                      key={d}
                      className={[
                        'group border-b border-r border-sand px-2 py-2 text-left text-xs font-normal',
                        isToday ? 'bg-terracotta/10 text-terracotta' : 'text-walnut',
                      ].join(' ')}
                    >
                      <span className="flex items-baseline justify-between gap-1">
                        <span>
                          {WEEKDAY_LABELS[d]}{' '}
                          <span className="text-stone">{dateOfWeekday(week, d).getDate()}</span>
                        </span>
                        <button
                          onClick={() =>
                            setCopying({
                              kind: 'day',
                              weekday: d,
                              label: `All of ${WEEKDAY_LABELS[d]}, every subject`,
                            })
                          }
                          title={`Copy all of ${WEEKDAY_LABELS[d]}`}
                          aria-label={`Copy all of ${WEEKDAY_LABELS[d]}`}
                          className="shrink-0 text-stone opacity-0 transition-opacity hover:text-terracotta focus:opacity-100 group-hover:opacity-100 print:hidden"
                        >
                          ⧉
                        </button>
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="align-top">
                  <th
                    className={[
                      'group relative border-b border-r border-sand px-2 py-2 text-left',
                      row.noStudents ? 'bg-sand/40' : 'bg-linen/50',
                    ].join(' ')}
                  >
                    <div
                      className={['text-xs', row.noStudents ? 'text-stone' : 'text-espresso'].join(
                        ' ',
                      )}
                    >
                      {row.label}
                    </div>
                    {row.timeStart && (
                      <div className="text-[10px] font-normal text-stone">
                        {row.timeStart}
                        {row.timeEnd ? `–${row.timeEnd}` : ''}
                      </div>
                    )}
                    {row.noStudents && (
                      <div className="mt-0.5 text-[9px] font-normal uppercase tracking-wide text-stone/70">
                        no students
                      </div>
                    )}
                    <button
                      onClick={() =>
                        setCopying({
                          kind: 'row',
                          rowId: row.id,
                          label: `${row.label}, all five days`,
                        })
                      }
                      title={`Copy ${row.label} to another week`}
                      aria-label={`Copy ${row.label} to another week`}
                      // Tucked into the corner and only revealed on hover — as a
                      // permanent line under every subject it added clutter to
                      // eight rows at once.
                      className="absolute right-1 top-1 rounded px-1 text-[11px] font-normal text-stone opacity-0 transition-opacity hover:text-terracotta focus:opacity-100 group-hover:opacity-100 print:hidden"
                    >
                      ⧉
                    </button>
                  </th>

                  {WEEKDAYS.map((d) => {
                    const cell = cells[row.id]?.[d];
                    const empty = cellIsEmpty(cell);
                    const isToday = isCurrentWeek && d === todayWeekday();

                    return (
                      <td
                        key={d}
                        className={[
                          'border-b border-r border-sand p-0',
                          row.noStudents ? 'bg-sand/25' : isToday ? 'bg-terracotta/[0.04]' : '',
                        ].join(' ')}
                      >
                        <div className="group relative h-full">
                          {!empty && (
                            <button
                              onClick={(ev) => {
                                ev.stopPropagation();
                                setCopying({
                                  kind: 'cell',
                                  rowId: row.id,
                                  weekday: d,
                                  label: `${row.label} on ${WEEKDAY_LABELS[d]}`,
                                });
                              }}
                              title="Copy this cell"
                              aria-label={`Copy ${row.label} on ${WEEKDAY_LABELS[d]}`}
                              className="absolute right-0.5 top-0.5 z-10 rounded bg-white/90 px-1 text-[10px] text-stone opacity-0 transition-opacity hover:text-terracotta group-hover:opacity-100 focus:opacity-100 print:hidden"
                            >
                              ⧉
                            </button>
                          )}
                          <button
                            onClick={() => setEditingCell({ row, weekday: d })}
                            className={[
                              'h-full min-h-[72px] w-full px-2 py-1.5 text-left align-top text-[11px] leading-snug transition-colors print:hover:bg-transparent',
                              row.noStudents ? 'hover:bg-sand/40' : 'hover:bg-linen/60',
                            ].join(' ')}
                          >
                            {empty ? (
                              <span className="text-stone print:hidden">+</span>
                            ) : cellMode(cell) === 'simple' ? (
                              /*
                                One block, no labels. A simple lesson is
                                writing rather than a filled-in form, and
                                putting a heading over it would put the form
                                back.
                              */
                              <span
                                className={[
                                  'rich-text-view block',
                                  row.noStudents ? 'text-walnut' : 'text-espresso',
                                ].join(' ')}
                                // Sanitised server-side on write; see
                                // server/src/services/sanitizeHtml.ts
                                dangerouslySetInnerHTML={{ __html: cell?.plan ?? '' }}
                              />
                            ) : (
                              <span className="block space-y-1">
                                {PLAN_FIELDS.map((field) => {
                                  // Standards collapse to their codes here. The
                                  // full wording belongs in the editor; on the
                                  // grid it would bury the lesson.
                                  const codes =
                                    field === 'standards'
                                      ? standardCodesLabel(cell?.standards)
                                      : null;
                                  if (field === 'standards' ? !codes : !stripHtml(cell?.[field]))
                                    return null;

                                  return (
                                    <span key={field} className="block">
                                      <span className="block text-[9px] uppercase tracking-wide text-stone/70">
                                        {PLAN_FIELD_LABELS[field]}
                                      </span>
                                      {codes !== null ? (
                                        <span className="block text-terracotta">{codes}</span>
                                      ) : (
                                        <span
                                          className={[
                                            'rich-text-view block',
                                            row.noStudents ? 'text-walnut' : 'text-espresso',
                                          ].join(' ')}
                                          // Sanitised server-side on write; see
                                          // server/src/services/sanitizeHtml.ts
                                          dangerouslySetInnerHTML={{ __html: cell?.[field] ?? '' }}
                                        />
                                      )}
                                    </span>
                                  );
                                })}
                              </span>
                            )}
                          </button>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </ResizableBoard>
      )}

      {editingCell && plan && (
        <PlanCellEditor
          week={week}
          row={editingCell.row}
          weekday={editingCell.weekday}
          cell={plan.cells[editingCell.row.id]?.[editingCell.weekday]}
          onClose={() => setEditingCell(null)}
        />
      )}

      {editingRows && plan && (
        <PlanRowEditor week={week} rows={plan.rows} onClose={() => setEditingRows(false)} />
      )}

      {copying && <CopyPlanMenu week={week} scope={copying} onClose={() => setCopying(null)} />}
    </div>
  );
}
